/**
 * AR invoice / AP bill upload from Excel (422471).
 *
 * Choose a file → Validate (a dry run: every invoice checked, nothing created) → Import. Each invoice
 * is created as a Draft through the same command the entry screen uses, then goes through the usual
 * submit / approve / post. One bad invoice never stops the rest; the summary says which and why.
 */
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Upload, Download, FileSpreadsheet, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { AppRoutes } from '@/constants/routes';
import { invoiceImportApi, type InvoiceImportKind, type InvoiceImportResult, type InvoiceImportRow } from '@/api/invoiceImport.api';
import { downloadInvoiceImportTemplate, parseInvoiceImportFile } from '@/utils/invoiceImportSheet';

interface Props { kind: InvoiceImportKind }

const readError = (e: unknown): string => {
  const res = (e as { response?: { data?: { errors?: Array<{ message?: string }>; message?: string } } }).response;
  return res?.data?.errors?.[0]?.message ?? res?.data?.message ?? (e instanceof Error ? e.message : 'That did not work.');
};

export const InvoiceImportPage = ({ kind }: Props) => {
  const { hasPermission } = usePermission();
  const canImport = hasPermission(kind === 'AR' ? Permissions.AR_INVOICE_CREATE : Permissions.AP_BILL_CREATE);
  const noun  = kind === 'AR' ? 'invoice' : 'bill';
  const party = kind === 'AR' ? 'Customer' : 'Supplier';
  const detailPath = kind === 'AR' ? AppRoutes.AR_INVOICE_DETAIL : AppRoutes.AP_BILL_DETAIL;

  const fileInput = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [rows, setRows]         = useState<InvoiceImportRow[]>([]);
  const [result, setResult]     = useState<InvoiceImportResult | null>(null);
  const [busy, setBusy]         = useState<'validate' | 'import' | null>(null);
  const [error, setError]       = useState<string | null>(null);

  const run = async (dryRun: boolean, data = rows) => {
    setBusy(dryRun ? 'validate' : 'import');
    setError(null);
    try { setResult(await invoiceImportApi.run(kind, data, dryRun)); }
    catch (e) { setError(readError(e)); }
    finally { setBusy(null); }
  };

  const choose = async (file: File | undefined) => {
    if (!file) return;
    setResult(null); setError(null); setFileName(file.name);
    try {
      const parsed = await parseInvoiceImportFile(kind, file);
      setRows(parsed);
      await run(true, parsed);   // validate straight away — nothing is created yet
    } catch (e) {
      setRows([]); setError(readError(e));
    } finally {
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const validated = result?.dryRun === true;
  const imported  = result?.dryRun === false;

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4 flex items-start gap-4">
        <div className="flex-1">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
            <FileSpreadsheet size={20} className="text-signal" /> Import {kind === 'AR' ? 'AR Invoices' : 'AP Bills'} from Excel
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            One row per line; rows with the same Invoice Ref become one {noun}. Each {noun} is created as a Draft
            and then submitted, approved and posted as usual.
          </p>
        </div>
        <Button variant="outline" size="sm" leftIcon={<Download size={14} />} onClick={() => downloadInvoiceImportTemplate(kind)}>
          Template
        </Button>
        {canImport && (
          <Button size="sm" leftIcon={<Upload size={14} />} loading={busy === 'validate'} onClick={() => fileInput.current?.click()}>
            Choose file
          </Button>
        )}
        <input ref={fileInput} type="file" accept=".xlsx,.xls,.csv" className="hidden"
          onChange={e => void choose(e.target.files?.[0])} />
      </div>

      <div className="flex-1 overflow-auto p-6 space-y-4">
        {error && (
          <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-300">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" /> {error}
          </div>
        )}

        {!result && !error && (
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-dashed border-slate-300 dark:border-slate-600 px-4 py-12 text-center text-sm text-slate-500">
            Download the template, fill it in, then choose the file. It is checked before anything is created.
          </div>
        )}

        {result && (
          <>
            <div className="flex items-center gap-3 flex-wrap bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 px-4 py-3">
              <span className="text-sm text-slate-700 dark:text-slate-200 font-medium">{fileName}</span>
              <span className="text-sm text-slate-500">{result.invoiceCount} {noun}s</span>
              <span className="text-sm text-emerald-700 dark:text-emerald-400">
                {result.okCount} {imported ? 'created as Draft' : 'ready'}
              </span>
              {result.failedCount > 0 && <span className="text-sm text-red-600 dark:text-red-400">{result.failedCount} with errors</span>}
              <div className="flex-1" />
              {validated && canImport && result.okCount > 0 && (
                <Button size="sm" loading={busy === 'import'} onClick={() => void run(false)}>
                  Import {result.okCount} {noun}{result.okCount === 1 ? '' : 's'}
                  {result.failedCount > 0 ? ' (skip the rest)' : ''}
                </Button>
              )}
              {imported && (
                <Link to={kind === 'AR' ? AppRoutes.AR_INVOICES : AppRoutes.AP_BILLS} className="text-sm text-signal hover:underline">
                  Go to {kind === 'AR' ? 'AR Invoices' : 'AP Bills'} to submit them
                </Link>
              )}
            </div>

            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    {['Invoice Ref', 'Row', party, 'Lines', 'Total', 'Result'].map(h => (
                      <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {result.invoices.map(i => (
                    <tr key={i.invoiceRef} className="align-top">
                      <td className="px-4 py-2.5 font-mono text-xs text-slate-900 dark:text-white">
                        {i.documentId ? <Link to={detailPath.replace(':id', i.documentId)} className="text-signal hover:underline">{i.invoiceRef}</Link> : i.invoiceRef}
                      </td>
                      <td className="px-4 py-2.5 text-xs text-slate-500">{i.firstRow}</td>
                      <td className="px-4 py-2.5 text-xs">{i.partyCode ?? '—'}</td>
                      <td className="px-4 py-2.5 text-xs">{i.lineCount}</td>
                      <td className="px-4 py-2.5 text-xs tabular-nums">{i.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                      <td className="px-4 py-2.5 text-xs">
                        {i.status === 'FAILED' ? (
                          <ul className="space-y-0.5 text-red-600 dark:text-red-400">
                            {i.errors.map(e => <li key={e} className="flex gap-1"><XCircle size={12} className="mt-0.5 shrink-0" />{e}</li>)}
                          </ul>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400">
                            <CheckCircle2 size={12} /> {i.status === 'CREATED' ? 'Created (Draft)' : 'Ready'}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
